import React, { useState, useEffect, useRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ToastrService from "../../services/toastService";
import Loader from "../loader/loader"
import Swal from "sweetalert2";
import { Logout, shouldLogout } from '../Common/Common'
import { useNavigate } from "react-router-dom";
import { useGetFlowByIntentIdMutation, useAddStepMutation, useUpdateStepMutation, useGetActiveIntentQuery, useManageQuestionsMutation } from "./Api";
import Tinymce from '../tinymce/Tinymce'
import { MdDeleteOutline } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa";
import Select from "react-select";

const AddSteps = ({
    mode = "add",
    onClose,
    onSave,
    data = null,
    questions,
    selectIntent,
    selectFlow,
}) => {

    const [addStepApi, { isLoading }] = useAddStepMutation()
    const [updateStepApi, { isLoading: updateIsLoading }] = useUpdateStepMutation()
    const { data: activeIntents, isLoading: activeIntentsIsLoading } = useGetActiveIntentQuery()
    const [getFlowByIntentIdApi, { isLoading: getFlowIsLoading }] = useGetFlowByIntentIdMutation()
    const [manageQuestionsApi, { isLoading: manageQuestionsIsloading }] = useManageQuestionsMutation()

    const navigate = useNavigate()
    const [activeIntent, setActiveIntent] = useState([])
    const [activeFlows, setActiveFlow] = useState([])
    const [deletedOptions, setDeletedOptions] = useState([]);

    const validationSchema = Yup.object().shape({
        step_order: Yup.string()
            .required('Question order is required')
            .matches(/^[0-9]+$/, 'Enter valid step order')
            .trim(),
        question: Yup.string()
            .required('Question is required')
            .trim(),
        solution: Yup.string().notRequired(),
        // is_final_step: Yup.string()
        //     .required('Is final step is required')
        //     .trim(),
        options: Yup.array().of(
            Yup.object().shape({
                option: Yup.string().notRequired(),

                is_final: Yup.string().when("option", {
                    is: (val) => val && val.trim() !== "",
                    then: (schema) => schema.required("Please select Yes/No"),
                    otherwise: (schema) => schema.notRequired(),
                }),

                next_step_order: Yup.string().when(["option", "is_final"], {
                    is: (option, is_final) =>
                        option && option.trim() !== "" && is_final === "no",
                    then: (schema) =>
                        schema.required("Next Step Order is required"),
                    otherwise: (schema) => schema.notRequired(),
                }),

                resolution: Yup.string().when(["option", "is_final"], {
                    is: (option, is_final) =>
                        option && option.trim() !== "" && is_final === "yes",
                    then: (schema) =>
                        schema.required("Resolution is required"),
                    otherwise: (schema) => schema.notRequired(),
                }),
            })
        )

    });

    // const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    //     resolver: yupResolver(validationSchema),
    //     mode: 'all'
    // });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        control,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(validationSchema),
        mode: "all",
        defaultValues: {
            options: [
                {
                    option: "",
                    is_final: "no",
                    next_step_order: "",
                    resolution: ""
                }
            ]
        }
    });

    const {
        fields,
        append,
        remove
    } = useFieldArray({
        control,
        name: "options"
    });

    // const selectedNextSteps = watch("options")?.map(opt => opt.next_step_order)?.filter(Boolean);
    const selectedNextSteps =
        watch("options")
            ?.filter(opt => opt.is_final === "no")
            ?.map(opt => opt.next_step_order)
            ?.filter(Boolean);


    useEffect(() => {
        if (data) {
            reset({
                step_order: data.step_order,
                question: data.question,
                solution: data.solution_text,
                // is_final_step: data.is_final_step === 1 ? "yes" : "no",

                // options:
                //     data.options?.length > 0
                //         ? data.options.reverse().map((opt) => ({
                //             id: opt.id,
                //             option: opt.option_label,
                //             is_final: opt.is_resolution ? "yes" : "no",
                //             next_step_order: opt.next_step_id || null,
                //             resolution: opt.resolution_text || "",
                //         }))
                //         : [
                //             {
                //                 option: "",
                //                 is_final: "no",
                //                 next_step_order: "",
                //                 resolution: "",
                //             },
                //         ],
                options:
                    data.options?.length > 0
                        ? [...data.options].reverse().map((opt) => ({
                            id: opt.id,
                            option: opt.option_label,
                            is_final: opt.is_resolution ? "yes" : "no",
                            next_step_order: opt.next_step_id || null,
                            resolution: opt.resolution_text || "",
                        }))
                        : [
                            {
                                option: "",
                                is_final: "no",
                                next_step_order: "",
                                resolution: "",
                            },
                        ],
            });
        }
    }, [data]);


    const resetButton = () => {
        reset({
            step_order: "",
            question: "",
            solution: "",
            is_final_step: "",
            options: [
                {
                    option: "",
                    is_final: "no",
                    next_step_order: "",
                    resolution: ""
                }
            ]
        });
    };

    const validateOptions = (options) => {
        const errors = {};

        if (!Array.isArray(options)) return errors;

        options.forEach((item, index) => {
            const option = item.option?.trim();
            const isFinal = item.is_final;
            const nextStep = item.next_step_order?.trim();
            const resolution = item.resolution?.trim();

            const isEmpty =
                !option && !isFinal && !nextStep && !resolution;

            // skip empty row
            if (isEmpty) return;

            const isValid =
                option &&
                isFinal &&
                (
                    (isFinal === "yes" && resolution) ||
                    (isFinal === "no" && nextStep)
                );

            if (!isValid) {
                errors[`options.${index}.row`] =
                    "Please complete the option row properly";
            }
        });

        return errors;
    };

    const onSubmit = async (payload) => {
        try {

            const cleanedOptions = payload.options
                .filter((item) => item.option && item.option.trim() !== "") // remove empty rows
                .map((item) => ({
                    ...(item.id ? { id: item.id } : {}),

                    option_label: item.option.trim(),
                    is_resolution: item.is_final === "yes",
                    next_step_id: item.next_step_order || null,
                    resolution_text: item.resolution?.trim() || ""
                }));

            if (payload.options.length > 1) {
                const optionErrors = validateOptions(payload.options);
                const hasErrors = Object.keys(optionErrors).length > 0;

                if (hasErrors) {
                    const firstError = Object.values(optionErrors)[0];
                    return ToastrService.error(firstError);
                }
            }

            if (mode == 'add') {

                const values = {
                    intent_id: selectIntent,
                    flow_id: selectFlow,
                    step_order: payload.step_order,
                    question: payload.question,
                    // is_final_step: payload.is_final_step == "yes" ? true : false,
                    solution_text: payload.solution,
                    options: cleanedOptions
                }
                const response = await manageQuestionsApi(values)

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    reset()
                    onSave()
                } else {
                    if (shouldLogout(response.error)) {
                        Logout(navigate);
                        return
                    }
                    ToastrService.error(response.error.data.message);
                }

            } else {

                const values = {
                    intent_id: selectIntent,
                    flow_id: selectFlow,
                    step_order: payload.step_order,
                    question_id: data.id,
                    question: payload.question,
                    // is_final_step: payload.is_final_step == "yes" ? true : false,
                    solution_text: payload.solution,
                    options: cleanedOptions,
                    remove_options: deletedOptions
                }

                const response = await manageQuestionsApi(values)

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    reset()
                    onSave()
                } else {
                    if (shouldLogout(response.error)) {
                        Logout(navigate);
                        return
                    }
                    ToastrService.error(response.error.data.message);
                }
            }
        } catch (err) {
            console.log(err, 'err')
        }
    }

    return (
        <div>
            {isLoading || updateIsLoading || activeIntentsIsLoading || getFlowIsLoading || manageQuestionsIsloading ? <Loader /> :
                <>
                    <div className="offcanvas show question-popup" style={{ visibility: "visible" }} tabIndex="-1">

                        <div className="offcanvas-header px-4 py-3">

                            <div>
                                <h3 className="mb-1 drawer-header">
                                    {mode === "add" ? "Create New Question" : "Manage Question"}
                                </h3>
                                <small className="text-muted">{mode === "add" ? "Fill in the details below to create a Question." : "Update the details below to Manage Question"}</small>
                            </div>

                            <button
                                type="button"
                                className="btn-close text-reset"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>

                        </div>


                        <div className="offcanvas-body px-4 py-3">

                            <form autoComplete="off" noValidate onSubmit={handleSubmit(onSubmit)}>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Question <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="question"
                                        name='question'
                                        placeholder='Enter the question'
                                        {...register('question')}
                                        className="form-control"

                                    />
                                    {errors.question && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.question.message}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Question Order <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="step_order"
                                        name='step_order'
                                        placeholder='Enter the question order'
                                        {...register('step_order')}
                                        className="form-control"

                                    />
                                    {errors.step_order && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.step_order.message}
                                        </p>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Solution
                                    </label>

                                    <Tinymce
                                        id="solution"
                                        value={watch("solution") || ""}
                                        onEditorChange={(content) => setValue("solution", content)}

                                    />

                                    {errors.solution && (
                                        <p
                                            className="mt-2"
                                            style={{
                                                color: "#d93025",
                                                fontFamily: "revert",
                                                marginLeft: "1%",
                                            }}
                                        >
                                            {errors.solution.message}
                                        </p>
                                    )}
                                </div>

                                {/* <div className="mb-3 final-step-radio-group">
                                    <label className="form-label">
                                        Is Final Step <span className="text-danger">*</span>
                                    </label>

                                    <div className="d-flex gap-4 align-items-center">

                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                id="final_no"
                                                value="no"
                                                {...register("is_final_step")}
                                            />
                                            <label className="form-check-label small" htmlFor="final_no">
                                                No
                                            </label>
                                        </div>

                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                id="final_yes"
                                                value="yes"
                                                {...register("is_final_step")}
                                            />
                                            <label className="form-check-label small" htmlFor="final_yes">
                                                Yes
                                            </label>
                                        </div>

                                    </div>
                                    {errors.is_final_step && (
                                        <p className="mt-2" style={{ color: "#d93025", fontFamily: "revert", marginLeft: '1%' }}>
                                            {errors.is_final_step.message}
                                        </p>
                                    )}
                                </div> */}

                                <div className="mb-3">
                                    <div className="card border-0 shadow-sm mb-2">
                                        <div className="card-body py-2">

                                            <div className="row align-items-center px-1">

                                                <div className="col-md-4">
                                                    <span className="form-label mb-0 text-muted fw-normal">
                                                        Option
                                                    </span>
                                                </div>

                                                <div className="col-md-2">
                                                    <span className="form-label mb-0 text-muted fw-normal">
                                                        Is Resolution
                                                    </span>
                                                </div>

                                                <div className="col-md-4">
                                                    <span className="form-label mb-0 text-muted fw-normal">
                                                        Resolution / Next Step Order
                                                    </span>
                                                </div>

                                                <div className="col-md-2 text-center">
                                                    <span className="form-label mb-0 text-muted fw-normal">
                                                        Action
                                                    </span>
                                                </div>

                                            </div>

                                        </div>
                                    </div>

                                    {/* {fields.map((field, index) => (
                                        <div
                                            key={field.id}
                                            className="row align-items-center mb-3"
                                        >

                                            <div className="col-md-4">
                                                <input
                                                    type="text"
                                                    placeholder="Enter option text"
                                                    className="form-control input-fixed"
                                                    {...register(`options.${index}.option`)}
                                                />
                                                {errors?.options?.[index]?.option && (
                                                    <small className="text-danger" >
                                                        {errors.options[index].option.message}
                                                    </small>
                                                )}
                                            </div>


                                            <div className="col-md-2">
                                                <select
                                                    className="form-select form-select-sm"
                                                    {...register(`options.${index}.is_final`)}
                                                >
                                                    <option value="no">No</option>
                                                    <option value="yes">Yes</option>
                                                </select>
                                            </div>

                                            <div className="col-md-4">
                                                {watch(`options.${index}.is_final`) === "yes" ? (
                                                    <textarea
                                                        placeholder="Resolution Text"
                                                        className="form-control input-fixed textarea-fixed"
                                                        {...register(`options.${index}.resolution`)}
                                                    />
                                                ) : (


                                                    <Controller
                                                        control={control}
                                                        name={`options.${index}.next_step_order`}
                                                        render={({ field }) => (
                                                            <Select
                                                                classNamePrefix="custom-select"
                                                                options={questions
                                                                    ?.filter(
                                                                        q =>
                                                                            !selectedNextSteps.includes(q.id) ||
                                                                            q.id === field.value // keep current selected value visible
                                                                    )
                                                                    .map(item => ({
                                                                        value: item.id,
                                                                        label: item.question,
                                                                    }))
                                                                }
                                                                value={
                                                                    questions
                                                                        ?.map(item => ({
                                                                            value: item.id,
                                                                            label: item.question,
                                                                        }))
                                                                        .find(opt => opt.value === field.value) || null
                                                                }
                                                                onChange={(selected) => field.onChange(selected?.value)}
                                                                maxMenuHeight={150}
                                                            />
                                                        )}
                                                    />
                                                )}
                                                {watch(`options.${index}.is_final`) === "no" &&
                                                    errors?.options?.[index]?.next_step_order && (
                                                        <small className="text-danger">
                                                            {errors.options[index].next_step_order.message}
                                                        </small>
                                                    )}
                                                {watch(`options.${index}.is_final`) === "yes" &&
                                                    errors?.options?.[index]?.resolution && (
                                                        <small className="text-danger">
                                                            {errors.options[index].resolution.message}
                                                        </small>
                                                    )}
                                            </div>


                                            <div className="col-md-2 d-flex justify-content-center">
                                                {fields.length > 1 ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => {
                                                            const removedItem = data?.options[index];

                                                            if (removedItem?.id) {
                                                                setDeletedOptions((prev) => [...prev, removedItem.id]);
                                                            }
                                                            remove(index);
                                                        }}
                                                    >
                                                        <FiTrash2 size={18} />
                                                    </button>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </div>
                                        </div>
                                    ))} */}

                                    {fields.map((field, index) => {
                                        const optionValue = watch(`options.${index}.option`);
                                        const isOptionFilled = optionValue?.trim() !== "";

                                        return (
                                            <div
                                                key={field.id}
                                                className="row  mb-3"
                                            >

                                                <div className="col-md-4">
                                                    <input
                                                        type="text"
                                                        placeholder="Enter option text"
                                                        className="form-control input-fixed"
                                                        {...register(`options.${index}.option`)}
                                                    />
                                                    {errors?.options?.[index]?.option && (
                                                        <small className="text-danger" >
                                                            {errors.options[index].option.message}
                                                        </small>
                                                    )}
                                                </div>


                                                <div className="col-md-2">
                                                    <select
                                                        className="form-select form-select-sm"
                                                        disabled={!isOptionFilled}
                                                        {...register(`options.${index}.is_final`)}
                                                    >
                                                        <option value="no">No</option>
                                                        <option value="yes">Yes</option>
                                                    </select>
                                                </div>

                                                <div className="col-md-4">
                                                    {watch(`options.${index}.is_final`) === "yes" ? (
                                                        <textarea
                                                            placeholder="Resolution Text"
                                                            className="form-control"
                                                            disabled={!isOptionFilled}
                                                            {...register(`options.${index}.resolution`)}
                                                        // rows={5}
                                                        />
                                                    ) : (
                                                        <Controller
                                                            control={control}
                                                            name={`options.${index}.next_step_order`}
                                                            render={({ field }) => (
                                                                <Select
                                                                    classNamePrefix="custom-select"
                                                                    isDisabled={!isOptionFilled}
                                                                    options={questions
                                                                        ?.filter(
                                                                            q =>
                                                                                q.question !== data?.question &&
                                                                                (
                                                                                    !selectedNextSteps.includes(q.id) ||
                                                                                    q.id === field.value
                                                                                )
                                                                        )
                                                                        .map(item => ({
                                                                            value: item.id,
                                                                            label: item.question,
                                                                        }))
                                                                    }
                                                                    value={
                                                                        questions
                                                                            ?.map(item => ({
                                                                                value: item.id,
                                                                                label: item.question,
                                                                            }))
                                                                            .find(opt => opt.value === field.value) || null
                                                                    }
                                                                    onChange={(selected) => field.onChange(selected?.value)}
                                                                    maxMenuHeight={150}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                    {watch(`options.${index}.is_final`) === "no" &&
                                                        errors?.options?.[index]?.next_step_order && (
                                                            <small className="text-danger">
                                                                {errors.options[index].next_step_order.message}
                                                            </small>
                                                        )}
                                                    {watch(`options.${index}.is_final`) === "yes" &&
                                                        errors?.options?.[index]?.resolution && (
                                                            <small className="text-danger">
                                                                {errors.options[index].resolution.message}
                                                            </small>
                                                        )}
                                                </div>


                                                <div className="col-md-2 d-flex">
                                                    {fields.length > 1 ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm align-self-start"
                                                            onClick={() => {
                                                                const removedItem = data?.options[index];

                                                                if (removedItem?.id) {
                                                                    setDeletedOptions((prev) => [...prev, removedItem.id]);
                                                                }
                                                                remove(index);
                                                            }}
                                                        >
                                                            <FiTrash2 size={18} />
                                                        </button>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() =>
                                            append({
                                                option: "",
                                                is_final: "no",
                                                next_step_order: "",
                                                resolution: ""
                                            })
                                        }
                                    >
                                        <FaPlus className="me-2" />   Add Option
                                    </button>
                                </div>


                                <div className="d-flex gap-2 mt-4">
                                    <button type="submit" className="btn btn-primary">
                                        {mode === "add" ? "Create Question" : "Update Question"}
                                    </button>

                                    {mode === "add" && (
                                        <button type="button" className="btn btn-secondary" onClick={resetButton}>
                                            Reset
                                        </button>
                                    )}
                                </div>

                            </form>

                        </div>
                    </div>

                    {/* Backdrop */}
                    <div className="offcanvas-backdrop fade show" onClick={onClose}></div>

                </>
            }
        </div >

    );
};

export default AddSteps;